// src/components/ExcusePopup.jsx
// Modal for excusing a Tier 1-3 Dual/Triple Interest finding, or updating
// an existing excuse (re-review). Current relationship amounts are shown
// but locked — they're a snapshot of fact, not something the reviewer
// edits. Reused for both "excuse this for the first time" (called from
// ConflictReportView) and "re-review" (called from the dedicated Reviewed
// Findings page, once built) via the optional existingExcuse prop.

import { useState, useEffect } from "react";

function formatMoney(n) {
  return `$${Math.round(n || 0).toLocaleString()}`;
}

export default function ExcusePopup({ finding, userId, fetchFn, existingExcuse = null, reportId = null, onClose, onSuccess }) {
  const isAuditThreshold = finding.signalType === "Payment Audit Threshold";
  const [thresholdGeneral, setThresholdGeneral] = useState(existingExcuse?.threshold_general_amount ?? "");
  const [thresholdOwnership, setThresholdOwnership] = useState(existingExcuse?.threshold_ownership_amount ?? "");
  const [thresholdResearch, setThresholdResearch] = useState(existingExcuse?.threshold_research_amount ?? "");
  const [thresholdDirectResearch, setThresholdDirectResearch] = useState(existingExcuse?.threshold_direct_research_amount ?? "");
  const [thresholdPiResearch, setThresholdPiResearch] = useState(existingExcuse?.threshold_pi_research_amount ?? "");
  const [regardlessOfAmount, setRegardlessOfAmount] = useState(existingExcuse?.excuse_regardless_of_amount || false);
  const [notes, setNotes] = useState(existingExcuse?.review_notes || "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Pre-fill from the account's standing Payment Audit Thresholds on a
  // fresh review only — a re-review already has its own prior values via
  // existingExcuse, which should take priority over the current account
  // default. This just saves re-typing the same numbers most of the time,
  // while still leaving every field editable for a provider-specific
  // exception.
  useEffect(() => {
    if (!isAuditThreshold || existingExcuse) return;
    fetchFn("/api/manage-audit-thresholds", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ userId, action: "get" }),
    })
      .then((res) => res.json())
      .then((data) => {
        const t = data.thresholds;
        if (!t) return;
        setThresholdGeneral((prev) => (prev === "" ? (t.general_payments_threshold ?? "") : prev));
        setThresholdOwnership((prev) => (prev === "" ? (t.ownership_threshold ?? "") : prev));
        setThresholdDirectResearch((prev) => (prev === "" ? (t.direct_research_threshold ?? "") : prev));
        setThresholdPiResearch((prev) => (prev === "" ? (t.pi_research_threshold ?? "") : prev));
      })
      .catch(() => {});
  }, []);

  async function handleConfirm() {
    if (saving) return;
    setSaving(true);
    setError(null);
    try {
      const res = await fetchFn("/api/manage-excuse", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          userId,
          action: "create",
          npi: finding.npi,
          companyId: finding.companyId || null,
          signalType: finding.signalType,
          tier: finding.tier,
          paymentAmount: finding.paymentAmount,
          ownershipAmount: finding.ownershipAmount,
          researchAmount: finding.researchAmount,
          directResearchAmount: finding.directResearchAmount,
          piResearchAmount: finding.piResearchAmount,
          providerName: finding.providerName,
          companyName: finding.companyName,
          thresholdGeneral: thresholdGeneral === "" ? null : parseFloat(thresholdGeneral),
          thresholdOwnership: thresholdOwnership === "" ? null : parseFloat(thresholdOwnership),
          thresholdResearch: thresholdResearch === "" ? null : parseFloat(thresholdResearch),
          thresholdDirectResearch: thresholdDirectResearch === "" ? null : parseFloat(thresholdDirectResearch),
          thresholdPiResearch: thresholdPiResearch === "" ? null : parseFloat(thresholdPiResearch),
          excuseRegardlessOfAmount: regardlessOfAmount,
          notes: notes.trim() || null,
          auditThresholdSnapshot: isAuditThreshold ? finding.auditThresholdSnapshot : null,
          reportId,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || "Failed to save excuse. Please try again.");
        return;
      }
      onSuccess(data.excuse);
    } catch {
      setError("Network error. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-navy-800 border border-white/10 rounded-xl p-5 max-w-md w-full space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div>
          <h3 className="text-white font-medium text-sm">
            {existingExcuse ? "Re-review this finding" : "Review this finding"}
          </h3>
          <p className="text-white/40 text-xs mt-0.5">
            {finding.signalType} — {finding.providerName || `NPI ${finding.npi}`} · {finding.companyName}
          </p>
        </div>

        <div className="bg-white/[0.03] border border-white/10 rounded-lg p-3 space-y-1">
          <p className="text-white/30 text-xs uppercase tracking-wide mb-1.5">
            {isAuditThreshold ? `Current relationship — ${new Date().getFullYear() - 1 === 2025 ? 2025 : new Date().getFullYear()}` : "Current relationship"}
          </p>
          {finding.paymentAmount > 0 && (
            <div className="flex justify-between text-xs">
              <span className="text-white/40">{isAuditThreshold ? "General payments" : "Payments"}</span>
              <span className="text-white/70 font-mono">{formatMoney(finding.paymentAmount)}</span>
            </div>
          )}
          {finding.ownershipAmount > 0 && (
            <div className="flex justify-between text-xs">
              <span className="text-white/40">Ownership</span>
              <span className="text-white/70 font-mono">{formatMoney(finding.ownershipAmount)}</span>
            </div>
          )}
          {isAuditThreshold ? (
            <>
              {finding.directResearchAmount > 0 && (
                <div className="flex justify-between text-xs">
                  <span className="text-white/40">Direct research</span>
                  <span className="text-white/70 font-mono">{formatMoney(finding.directResearchAmount)}</span>
                </div>
              )}
              {finding.piResearchAmount > 0 && (
                <div className="flex justify-between text-xs">
                  <span className="text-white/40">PI research</span>
                  <span className="text-white/70 font-mono">{formatMoney(finding.piResearchAmount)}</span>
                </div>
              )}
            </>
          ) : (
            finding.researchAmount > 0 && (
              <div className="flex justify-between text-xs">
                <span className="text-white/40">Research</span>
                <span className="text-white/70 font-mono">{formatMoney(finding.researchAmount)}</span>
              </div>
            )
          )}
        </div>

        <label className="flex items-start gap-2 text-xs text-white/60 cursor-pointer">
          <input
            type="checkbox"
            checked={regardlessOfAmount}
            onChange={(e) => setRegardlessOfAmount(e.target.checked)}
            className="mt-0.5 rounded border-white/20 bg-white/5"
          />
          <span>
            No review required regardless of future amount
            <span className="block text-white/30 mt-0.5">This relationship will stay in Reviewed status no matter how large it grows. Use with care.</span>
          </span>
        </label>

        {!regardlessOfAmount && (
          <div className="space-y-2">
            <p className="text-white/40 text-xs">Re-review if any category reaches or exceeds:</p>
            <div className="grid grid-cols-1 gap-2">
              <label className="flex items-center justify-between gap-2 text-xs text-white/50">
                <span>General payments</span>
                <input
                  type="number"
                  placeholder="blank = any"
                  value={thresholdGeneral}
                  onChange={(e) => setThresholdGeneral(e.target.value)}
                  className="w-32 bg-white/5 border border-white/10 rounded px-2 py-1 text-white text-xs text-right focus:outline-none focus:border-red-500/40"
                />
              </label>
              <label className="flex items-center justify-between gap-2 text-xs text-white/50">
                <span>Ownership value</span>
                <input
                  type="number"
                  placeholder="blank = any"
                  value={thresholdOwnership}
                  onChange={(e) => setThresholdOwnership(e.target.value)}
                  className="w-32 bg-white/5 border border-white/10 rounded px-2 py-1 text-white text-xs text-right focus:outline-none focus:border-red-500/40"
                />
              </label>
              {isAuditThreshold ? (
                <>
                  <label className="flex items-center justify-between gap-2 text-xs text-white/50">
                    <span>Direct research</span>
                    <input
                      type="number"
                      placeholder="blank = any"
                      value={thresholdDirectResearch}
                      onChange={(e) => setThresholdDirectResearch(e.target.value)}
                      className="w-32 bg-white/5 border border-white/10 rounded px-2 py-1 text-white text-xs text-right focus:outline-none focus:border-red-500/40"
                    />
                  </label>
                  <label className="flex items-center justify-between gap-2 text-xs text-white/50">
                    <span>PI research</span>
                    <input
                      type="number"
                      placeholder="blank = any"
                      value={thresholdPiResearch}
                      onChange={(e) => setThresholdPiResearch(e.target.value)}
                      className="w-32 bg-white/5 border border-white/10 rounded px-2 py-1 text-white text-xs text-right focus:outline-none focus:border-red-500/40"
                    />
                  </label>
                </>
              ) : (
                <label className="flex items-center justify-between gap-2 text-xs text-white/50">
                  <span>Research funding</span>
                  <input
                    type="number"
                    placeholder="blank = any"
                    value={thresholdResearch}
                    onChange={(e) => setThresholdResearch(e.target.value)}
                    className="w-32 bg-white/5 border border-white/10 rounded px-2 py-1 text-white text-xs text-right focus:outline-none focus:border-red-500/40"
                  />
                </label>
              )}
            </div>
          </div>
        )}

        <div>
          <p className="text-white/40 text-xs mb-1">Review notes (optional)</p>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-xs focus:outline-none focus:border-red-500/40 resize-none"
            placeholder="What did your review determine?"
          />
        </div>

        {error && <p className="text-red-400 text-xs">{error}</p>}

        <div className="flex items-center justify-end gap-2 pt-1">
          <button onClick={onClose} className="text-white/40 hover:text-white/70 text-xs px-3 py-1.5">
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={saving}
            className="text-xs px-4 py-1.5 rounded-lg bg-red-500/10 text-red-400 hover:bg-red-500/20 disabled:opacity-50 transition-all font-medium"
          >
            {saving ? "Saving…" : existingExcuse ? "Update Review" : "Confirm Review"}
          </button>
        </div>
      </div>
    </div>
  );
}
